import React from 'react';

import Yup from 'yup';
import SessionStore from 'stores/SessionStore';
import CacheService from 'service/CacheService';

import AppConstants from 'AppConstants';
import AppUtil from 'AppUtil';

import OrderForm from 'views/order/OrderForm';

import OrderSizeInput from 'views/order/input/OrderSizeInput';
import QuantityInput from 'views/order/input/QuantityInput';
import ProductUtil from 'util/ProductUtil';

const DELIVERY_LOCATION_TYPE = AppConstants.DELIVERY_LOCATION_TYPE;

const OUTSIDE_KTM_VALLEY = DELIVERY_LOCATION_TYPE.OUTSIDE_KTM_VALLEY;
const INSIDE_KTM_VALLEY = DELIVERY_LOCATION_TYPE.INSIDE_KTM_VALLEY;

const INSIDE_RING_ROAD = DELIVERY_LOCATION_TYPE.INSIDE_RING_ROAD;
const OUTSIDE_RING_ROAD = DELIVERY_LOCATION_TYPE.OUTSIDE_RING_ROAD;

const PRODUCT_TYPES = AppConstants.PRODUCT_TYPES;
const USER_TYPE = AppConstants.USER_TYPE;

const DELIVERY_TYPE = AppConstants.DELIVERY_TYPE;

function getDefaultLocationType(productType, savedAddress) {
    if(productType === AppConstants.PRODUCT_TYPES['PHOTO_FRAME'].val) {
        return INSIDE_KTM_VALLEY.val;
    } else {
        if(savedAddress && savedAddress.locationType)
            return savedAddress.locationType;
    }

    return '';
}

class ProductOrderForm extends React.Component {
    constructor(props) {
        super(props);

        this.initialValues = this.initialValues.bind(this);
        this.validationScheme = this.validationScheme.bind(this);
        this.createOrderRequest = this.createOrderRequest.bind(this);
		this.isPrePaymentRequired = this.isPrePaymentRequired.bind(this);
    }

    initialValues() {
        if(!this.props.product) return {};

        const currentSession = SessionStore.getCurrentSession();
		const currentUser = currentSession? currentSession.user: null;

		const isSalesUser = currentUser && (currentUser.userType ===  USER_TYPE.ADMIN || currentUser.userType ===  USER_TYPE.SALES);
		const savedAddress = JSON.parse(CacheService.get(AppConstants.USER_ADDRESS_KEY));

		const product = this.props.product;

		const initLocationType = getDefaultLocationType(product.productType, savedAddress);
		const initDeliveryType = AppUtil.getDefaultDeliveryType(product.productType, initLocationType);

		let productImage = '';
		let selectedImage = '';
		if(this.props.selectedImage) {
			productImage = this.props.selectedImage;
			selectedImage = this.props.selectedImage;
		} else {
			productImage = AppUtil.getDefaultProductImage(product);

			//Note: preview image may not be available and only product image can be available.
			if(product.previewImage) selectedImage = product.previewImage;
			else if(productImage) selectedImage = productImage;
			else selectedImage = product.productImages[0];
		};

		const userOrder = {
			quantity: 1,
			productImage: productImage,
			selectedImage: selectedImage,
			phone: savedAddress? savedAddress.phone: '',
			streetAddress: savedAddress? savedAddress.streetAddress: '',
			additionalNote: '',
			locationType: initLocationType,
			deliveryType: initDeliveryType,
			productPrice: product.price,
			customerName: (!isSalesUser && currentUser)? currentUser.username: '',
			receiveEmailNotification: false,
			customerEmail: (!isSalesUser && currentUser && currentUser.emailAddress)? currentUser.emailAddress: '',
			paymentPreference: 'COD',
			hasCouponCode: false,
			couponCode: '',

			//for bulk order
			isBulkOrderForm: false,
			sizeQuantity: {}
		};

		userOrder.productSize = '';
		const productVariants = this.props.product.productVariants;
		if(productVariants) {
			productVariants.forEach((v) => {

				const variantKey = ProductUtil.getProductVariantKey(v);
				userOrder[variantKey] = v.variants[0].variantId;
			});
		}

        return userOrder;
    }

    validationScheme() {
        if(!this.props.product) return {};

		const productType = PRODUCT_TYPES[this.props.product.productType];

        const validation = {
			quantity: Yup.number().typeError("Enter a quantity").min(1, "Enter a positive quantity.").required("Enter a quantity"),
			isBulkOrderForm: Yup.boolean()
		};

        validation.productSize = Yup.string().when('isBulkOrderForm', (isBulkOrderForm, schema) => {
									if(!isBulkOrderForm)
										return Yup.string().required("Select a preferrable size.");

									return Yup.string();
								});

		// validation.productSize = Yup.string().required("Select a preferrable size.");

		validation.locationType = Yup.string().required("Where are you located?");
		validation.paymentPreference = Yup.string().when(['locationType', 'isBulkOrderForm'], (locationType, isBulkOrderForm, schema) => {
										return this.isPrePaymentRequired({locationType: locationType, isBulkOrderForm: isBulkOrderForm})? 
																			Yup.string().required("Select one option to pay."): 
																			Yup.string();
									});
		
		validation.streetAddress = Yup.string().required("Enter your delivery street address.");
		validation.phone = AppUtil.phoneNumberValidation();
		validation.customerName = Yup.string().required("Enter your Full Name");
		validation.receiveEmailNotification = Yup.boolean();
		validation.customerEmail = Yup.boolean().when('receiveEmailNotification', (receiveEmailNotification, schema) => {
				
				if(receiveEmailNotification)
					return Yup.string().required("Enter a valid email address.").email('Invalid Email address');
				
				return Yup.string();
		});

		validation.deliveryType = Yup.string().when('locationType', (locationType, schema) => {
			if(locationType === INSIDE_KTM_VALLEY.val 
				|| locationType === OUTSIDE_RING_ROAD.val 
				|| locationType === INSIDE_RING_ROAD.val) {
					const yupValidation = Yup.string().required("Specify how you would like to get it delivered.");
					if(productType.hasImmediateDelivery)
						return yupValidation;
					else
						return yupValidation.matches(/(REGULAR)/, "Immediate delivery not supported on "+productType.label+".");
				}
				
			return Yup.string();
		});

		validation.hasCouponCode = Yup.boolean();
		validation.couponCode = Yup.boolean().when('hasCouponCode', (hasCouponCode, schema) => {
			if(hasCouponCode)
				return Yup.string().required("Enter a coupon code");

			return Yup.string();
		});

		
		validation.productImage = Yup.string().required("Select which "+productType.label+" you want.");

		return validation;
    }


    createOrderRequest(formValue) {
        let order = {};
		order.productId = this.props.product.id;

		order.isBulkOrder = formValue.isBulkOrderForm;
		if(formValue.isBulkOrderForm === true) {
			order.sizeQuantity = formValue.sizeQuantity;
		} else {
			order.productSizes = [];
        	order.productSizes.push(formValue.productSize);
			order.quantity = formValue.quantity;
		}
		

		order.orderedProductImage = formValue.productImage;

		order.phone = formValue.phone.trim();
		order.locationType = formValue.locationType;
		order.deliveryCharge = AppUtil.getLocationExtraCost(order.locationType, this.props.product);
		order.streetAddress = formValue.streetAddress.trim();
		order.additionalNote = formValue.additionalNote;
		order.deliveryType = formValue.deliveryType;
		order.paymentPreference = formValue.paymentPreference;
		order.customerName = formValue.customerName.trim();
		order.customerEmail = formValue.receiveEmailNotification? formValue.customerEmail.trim(): null;
		order.couponCode = formValue.hasCouponCode? formValue.couponCode: null;
		order.paymentPreference = formValue.paymentPreference;

		const productVariants = this.props.product.productVariants;

		if(productVariants) {
			order.selectedVariants = [];
			productVariants.forEach((v) => {
				const variantKey = ProductUtil.getProductVariantKey(v);
				order.orderMetaData = order.orderMetaData || {};

				const selectedVariantValue = formValue[variantKey];
				if(selectedVariantValue) {
					order.selectedVariants.push(selectedVariantValue);
					const selectedVariantLabel = v.variants.filter(vr => vr.variantId === selectedVariantValue)[0].label;
					order.orderMetaData[v.variantLabel] =  selectedVariantLabel;
				}
				
			});
		}
		
		return order;
    }


    displayDeliveryInputs(formValue) {
		const isBulkOrder = formValue.isBulkOrderForm;

		if(isBulkOrder) {
			const sizeQuantity = formValue.sizeQuantity;
			return Object.keys(sizeQuantity).length > 0;
		} else {
			const quantity = formValue.quantity;
        	const size = formValue.productSize;
			return !isNaN(quantity) && size !== '';
		}
    }

	isPrePaymentRequired(formValue) {
		return formValue.locationType === OUTSIDE_KTM_VALLEY.val || formValue.isBulkOrderForm;
	}

    render() {
        const initialValues = this.initialValues();
        const validationScheme = this.validationScheme();

        return <OrderForm initialValues={initialValues} validationScheme={validationScheme}
                product={this.props.product} 
                createOrderRequest={this.createOrderRequest}
                displayDeliveryInputs={this.displayDeliveryInputs} 
				isPrePaymentRequired={this.isPrePaymentRequired} 
                render={() => {
                    return <React.Fragment>
                            <QuantityInput />
                            <OrderSizeInput />
                    </React.Fragment>
            }
        }/>
    }
}


export default ProductOrderForm;


// WEBPACK FOOTER //
// ./src/views/order/ProductOrderForm.js